import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
  Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, 
  Globe, Building2, X, RefreshCw 
} from 'lucide-react';
import toast from 'react-hot-toast';

import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const ContractList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // --- States ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgsList, setOrgsList] = useState([]);
  const [showAllSites, setShowAllSites] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ contract_num: "", description: "", status: "", contract_type: "" });
  const [sortConfig, setSortConfig] = useState({ key: 'contract_num', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 15;

  const session = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const { canCreate } = usePermissions('contracts');
  const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';
  const { fontSize } = useFontSize();

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const forceAll = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAll = showAllSites || forceAll;

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        all_sites: Boolean(effectiveAll),
        q: searchTerm,
        ...filters,
        org_id: selectedOrg || session?.org_id,
        site_id: effectiveAll ? (selectedSite || filters.site_id) : session?.site_id,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction
      };

      const res = await axios.get('/api/contracts', { 
        headers: { 'Authorization': `Bearer ${token}` },
        params 
      });

      setData(res.data.data || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error(t('contract.messages.fetchError')); // 🎯
    } finally { 
      setLoading(false); 
    }
  }, [currentPage, searchTerm, filters, selectedOrg, selectedSite, showAllSites, sortConfig, isSuperAdmin, session?.org_id, session?.site_id, t]);

  useEffect(() => { 
    fetchContracts(); 
  }, [fetchContracts]);

  useEffect(() => {
    if (isSuperAdmin) {
      const token = localStorage.getItem('token');
      axios.get('/api/organizations', {
          headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => setOrgsList(res.data)).catch(err => console.error(err));
    }
  }, [isSuperAdmin]);

  const handleRowClick = (num) => {
    const query = new URLSearchParams({ ...filters, searchTerm, all_sites: showAllSites }).toString();
    navigate(`/contracts/${num}?${query}`);
  };

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(1);
  };

  useEffect(() => {
    const openAll = () => { if(isSuperAdmin) { setShowAllSites(true); setCurrentPage(1); } };
    const viewMine = () => { setShowAllSites(false); setCurrentPage(1); };
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
    };
  }, [isSuperAdmin]);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return (
    <div 
      className={`flex flex-col h-full bg-surface-app font-bold uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right dir-rtl' : 'font-sans text-left'}`}
      style={{ fontSize: `${fontSize}px` }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Search Header Panel */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-10 space-y-3 px-6 shrink-0">
        <div className={`flex justify-between items-center `}>
          <div className={`flex items-center gap-6 `}>
            <h2 className={`text-primary font-black italic text-xl flex items-center gap-2 uppercase `}>
              <RefreshCw size={22} className={`${loading ? 'animate-spin text-primary' : 'text-primary-dark cursor-pointer'}`} onClick={() => fetchContracts()} />
              {t('contract.headers.masterNode')}
            </h2>
            <div className="relative group">
              <Search className={`absolute top-2.5 text-ink-secondary group-focus-within:text-primary ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
              <input
                className={`py-2 border border-line w-[450px] outline-none focus:border-primary rounded-sm font-black text-primary-dark shadow-inner ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'}`}
                placeholder={t('contract.placeholders.globalSearch')}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>
          {canCreate && (
            <button onClick={() => navigate('/contracts/new')} className={`bg-primary-dark text-white px-8 py-2.5 rounded-sm font-black flex items-center gap-2 hover:bg-black shadow-lg transition-all active:scale-95 `}>
              <Plus size={18} strokeWidth={3} /> {t('contract.actions.newContract')}
            </button>
          )}
        </div>

        {isSuperAdmin && (
          <div className={`flex items-center gap-4 bg-surface-panel p-2 rounded-sm border w-fit border-line shadow-sm animate-in fade-in ${isRTL ? 'float-right' : ''}`}>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-4' : 'border-r pr-4'}`}>
              <Globe size={14} className="text-primary"/>
              <select className={`bg-transparent outline-none text-[10px] font-black uppercase cursor-pointer ${isRTL ? 'dir-rtl text-right' : 'text-left'}`} value={selectedOrg} onChange={e => { setSelectedOrg(e.target.value); setCurrentPage(1); }}>
                <option value="">{t('assets.list.all_orgs')}</option>
                {orgsList.map(o => <option key={o.org_id} value={o.org_id}>{o.org_id}</option>)}
              </select>
            </div>
            <div className={`flex items-center gap-2 `}>
              <Building2 size={14} className="text-primary"/>
              <select
                className={`bg-transparent outline-none text-[10px] font-black uppercase cursor-pointer disabled:opacity-30 ${isRTL ? 'dir-rtl text-right' : 'text-left'}`}
                value={selectedSite}
                onChange={e => { setSelectedSite(e.target.value); setCurrentPage(1); }}
                disabled={!selectedOrg}
              >
                <option value="">{t('assets.list.all_sites')}</option>
                {orgsList.find(o => o.org_id === selectedOrg)?.sites?.map(s => <option key={s.site_id} value={s.site_id}>{s.site_id}</option>)}
              </select>
            </div>
            {(selectedOrg || searchTerm || filters.contract_num) && (
              <button
                onClick={() => {
                    setSelectedOrg(""); setSelectedSite(""); setSearchTerm("");
                    setFilters({contract_num: "", description: "", status: "", contract_type: ""});
                    setCurrentPage(1);
                }}
                className={`text-[10px] text-status-danger font-black uppercase hover:bg-status-danger-bg px-3 py-1 rounded transition-all flex items-center gap-1 border border-status-danger-bg shadow-sm `}
              >
                <X size={12}/> {t('common.clearAll')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Grid Content */}
      <div className="flex-1 p-6 overflow-auto custom-scrollbar bg-surface-panel">
        <div className="bg-surface-card border-2 border-line rounded shadow-2xl overflow-hidden min-w-max">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-black uppercase sticky top-0 z-20 shadow-md">
              <tr className={isRTL ? 'flex-row-reverse' : ''}>
                <th className={`p-4 border-x border-white/10 w-48 cursor-pointer hover:bg-white/10 transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => requestSort('contract_num')}>
                   <div className={`flex items-center justify-between `}>{t('contract.tables.contractNum')} <ArrowUpDown size={12}/></div>
                </th>
                <th className="p-4 border-x border-white/10">{t('assets.headers.desc')}</th>
                <th className="p-4 border-x border-white/10 w-32 text-center">{t('locationDetails.thType')}</th>
                <th className="p-4 border-x border-white/10 w-32 text-center">{t('assets.headers.status')}</th>
                <th className={`p-4 border-x w-48 ${isRTL ? 'text-left' : 'text-right'}`}>{t('contract.tables.grandTotal')}</th>
              </tr>
              {/* Column Filters */}
              <tr className={`bg-surface-panel shadow-inner `}>
                <td className="p-1.5 border-x border-line">
                    <input className={`w-full border border-line p-1 font-black text-primary-dark outline-none focus:border-primary ${isRTL ? 'text-right' : ''}`} value={filters.contract_num} onChange={e => { setFilters({...filters, contract_num: e.target.value}); setCurrentPage(1); }} placeholder={t('common.filter')} />
                </td>
                <td className="p-1.5 border-x border-line"></td>
                <td className="p-1.5 border-x border-line">
                   <select className={`w-full border border-line p-1 outline-none text-primary-dark font-black text-[10px] ${isRTL ? 'text-right dir-rtl' : ''}`} value={filters.contract_type} onChange={e => { setFilters({...filters, contract_type: e.target.value}); setCurrentPage(1); }}>
                      <option value="">{t('contract.options.allTypes')}</option>
                      <option value="LABOR">LABOR/SERVICE</option>
                      <option value="SUPPLY">SUPPLY/PARTS</option>
                      <option value="LEASE">LEASE/PROPERTY</option>
                   </select>
                </td>
                <td className="p-1.5 border-x border-line">
                   <select className={`w-full border border-line p-1 outline-none text-primary-dark font-black text-[10px] ${isRTL ? 'text-right dir-rtl' : ''}`} value={filters.status} onChange={e => { setFilters({...filters, status: e.target.value}); setCurrentPage(1); }}>
                      <option value="">{t('contract.options.allStatus')}</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="DRAFT">DRAFT</option>
                      <option value="APPROVED">APPROVED</option>
                   </select>
                </td>
                <td className="p-1.5 border-x"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr><td colSpan="5" className="p-32 text-center font-black animate-pulse tracking-[0.5em] text-primary-dark bg-surface-panel">{t('contract.messages.syncing')}</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="5" className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest">{t('contract.messages.noData')}</td></tr>
              ) : data.map((c, i) => (
                <tr key={i} onClick={() => handleRowClick(c.contract_num)} className={`hover:bg-primary-soft cursor-pointer group transition-all `}>
                  <td className={`p-4 border-x border-line-subtle font-black text-primary-dark group-hover:text-primary transition-colors text-[13px] ${isRTL ? 'text-right' : 'text-left'}`}>{c.contract_num}</td>
                  <td className={`p-4 border-x border-line-subtle text-ink font-bold italic ${isRTL ? 'text-right' : 'text-left'}`}>{c.description}</td>
                  <td className="p-4 border-x border-line-subtle text-center font-black text-ink-secondary text-[10px] italic">{c.contract_type}</td>
                  <td className="p-4 border-x border-line-subtle text-center">
                    <span className={`px-4 py-1 rounded-sm text-[9px] font-black shadow-sm ring-1 ring-inset ${c.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success ring-status-success/20' : 'bg-surface-panel text-ink-secondary ring-ink-secondary/20'}`}>{c.status}</span>
                  </td>
                  <td className={`p-4 border-x font-black text-primary-dark bg-primary-soft/20 italic tracking-widest text-[14px] ${isRTL ? 'text-left' : 'text-right'}`}>
                    {c.total_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-[9px] opacity-40 mx-1">{c.currency}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className={`bg-primary-dark text-white p-3 px-10 flex justify-between items-center shadow-2xl z-40 border-t border-white/10 shrink-0 `}>
        <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-[0.2em] `}>
          <div className={`flex items-center gap-2 `}>
            <span className="text-sky italic">{t('contract.footer.masterRecords')}</span>
            <span className="bg-black/20 px-4 py-1.5 rounded-sm shadow-inner border border-white/5 text-status-warning text-sm font-black">{totalItems}</span>
          </div>
          <div className={`flex items-center gap-2 border-white/10 ${isRTL ? 'border-r pr-10 flex-row-reverse' : 'border-l pl-10'}`}>
            <span className="text-sky italic">{t('contract.footer.currentPage')}</span>
            <span className="text-white text-[12px] mx-2">{currentPage} / {totalPages || 1}</span>
          </div>
        </div>
        <div className={`flex items-center gap-3 `}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            className="p-2.5 border border-white/20 rounded-sm hover:bg-status-warning transition-all active:scale-90 disabled:opacity-10 bg-white/5 shadow-md"
          >
            <ChevronLeft size={22} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            className="p-2.5 border border-white/20 rounded-sm hover:bg-status-warning transition-all active:scale-90 disabled:opacity-10 bg-white/5 shadow-md"
          >
            <ChevronRight size={22} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractList;